// src/pages/AdminRevenuePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient.js";

import DashboardSidebarAdmin from "../components/Dashboard/DashboardSidebarAdmin.jsx";
import DashboardNavbarAdmin from "../components/Dashboard/DashboardNavbarAdmin.jsx";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const PAGE_SIZE = 10;

const AdminRevenuePage = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const [entries, setEntries] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const displayName =
    profile?.username || user?.email?.split("@")[0] || "Admin";

  const formatRupiah = (value) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(value || 0);

  const formatDateTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString("id-ID", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  // ---------- cek user admin ----------
  useEffect(() => {
    let isMounted = true;

    const fetchUser = async () => {
      try {
        setLoadingUser(true);

        const { data, error: authError } = await supabase.auth.getUser();
        const authUser = data?.user;

        if (authError || !authUser) {
          navigate("/login");
          return;
        }

        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", authUser.id)
          .single();

        if (profileError && profileError.code !== "PGRST116") {
          throw profileError;
        }

        if (!profileData || !profileData.phone) {
          navigate("/login");
          return;
        }

        if (profileData.role !== "admin") {
          navigate("/dashboard");
          return;
        }

        if (!isMounted) return;

        setUser(authUser);
        setProfile(profileData);
      } catch (err) {
        console.error("Error fetching admin data:", err);
      } finally {
        if (isMounted) setLoadingUser(false);
      }
    };

    fetchUser();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  // ---------- load revenue ledger + join profiles ----------
  const loadRevenue = async () => {
    try {
      setLoadingData(true);
      setError(null);

      const { data: ledgerRows, error: ledgerErr } = await supabase
        .from("revenue_ledger")
        .select(
          "id, transaction_id, wallet_owner_id, amount, description, created_at"
        )
        .order("created_at", { ascending: false })
        .limit(500);

      if (ledgerErr) throw ledgerErr;

      if (!ledgerRows || ledgerRows.length === 0) {
        setEntries([]);
        return;
      }

      const walletIds = Array.from(
        new Set(
          ledgerRows
            .map((r) => r.wallet_owner_id)
            .filter((id) => !!id)
        )
      );

      const { data: profilesData, error: profilesErr } = await supabase
        .from("profiles")
        .select("id, username, email")
        .in(
          "id",
          walletIds.length
            ? walletIds
            : ["00000000-0000-0000-0000-000000000000"]
        );

      if (profilesErr) throw profilesErr;

      const profilesMap = new Map();
      (profilesData || []).forEach((p) => {
        profilesMap.set(p.id, p);
      });

      const enriched = ledgerRows.map((r) => {
        const owner = r.wallet_owner_id
          ? profilesMap.get(r.wallet_owner_id)
          : null;
        const ownerName =
          owner?.username ||
          owner?.email ||
          (r.wallet_owner_id ? r.wallet_owner_id : "-");

        return {
          ...r,
          ownerName,
          ownerEmail: owner?.email || null,
        };
      });

      setEntries(enriched);
    } catch (err) {
      console.error("Error loading revenue ledger:", err);
      setError(err.message || "Gagal memuat data revenue.");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (!loadingUser && user) {
      loadRevenue();
    }
  }, [loadingUser, user]);

  // ---------- filter tanggal ----------
  const filteredEntries = useMemo(() => {
    let result = entries;

    if (startDate) {
      const start = new Date(startDate + "T00:00:00");
      result = result.filter((e) => new Date(e.created_at) >= start);
    }

    if (endDate) {
      const end = new Date(endDate + "T23:59:59.999");
      result = result.filter((e) => new Date(e.created_at) <= end);
    }

    return result;
  }, [entries, startDate, endDate]);

  // ---------- total amount dari data yang sudah terfilter ----------
  const totalFilteredAmount = useMemo(
    () =>
      filteredEntries.reduce(
        (sum, entry) => sum + (Number(entry.amount) || 0),
        0
      ),
    [filteredEntries]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [startDate, endDate]);

  // ---------- pagination ----------
  const totalPages = pageSize === 'all'
    ? 1
    : Math.max(1, Math.ceil(filteredEntries.length / pageSize) || 1);

  const paginatedEntries = useMemo(() => {
    if (pageSize === 'all') return filteredEntries;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredEntries.slice(startIndex, endIndex);
  }, [filteredEntries, currentPage, pageSize]);

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handleResetFilter = () => {
    setStartDate("");
    setEndDate("");
  };

  // ---------- download PDF ----------
  const handleDownloadPdf = () => {
    if (filteredEntries.length === 0) return;

    const doc = new jsPDF("p", "mm", "a4");
    doc.setFontSize(14);
    doc.text("Revenue Ledger", 14, 16);

    let subtitle = "Semua data";
    if (startDate || endDate) {
      subtitle = `Periode: ${startDate || "awal"} s/d ${endDate || "sekarang"
        }`;
    }

    doc.setFontSize(10);
    doc.text(subtitle, 14, 22);

    // Susunan kolom PDF:
    // No | Tanggal | Transaksi | Wallet Owner | Deskripsi | Amount
    const rows = filteredEntries.map((e, idx) => [
      idx + 1, // No
      formatDateTime(e.created_at),
      e.transaction_id || "-",
      e.ownerName || "-",
      e.description || "-",        // Deskripsi
      formatRupiah(e.amount),      // Amount
    ]);

    // Tambah 1 baris terakhir untuk total:
    // Deskripsi = "Total", Amount = total
    rows.push([
      "",             // No
      "",             // Tanggal
      "",             // Transaksi
      "",             // Wallet Owner
      "Total",        // Deskripsi
      formatRupiah(totalFilteredAmount), // Amount
    ]);

    autoTable(doc, {
      head: [
        ["No", "Tanggal", "Transaksi", "Wallet Owner", "Deskripsi", "Amount"],
      ],
      body: rows,
      startY: 26,
      styles: { fontSize: 8, cellWidth: "wrap" },
      headStyles: { fillColor: [15, 76, 129] },
    });

    doc.save("revenue-ledger.pdf");
  };

  // ---------- loading user ----------
  if (loadingUser) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mb-3" />
        <p className="text-slate-500">Memuat admin dashboard...</p>
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-50 font-sans overflow-hidden">
      <div className="flex h-full min-w-0">
        {/* SIDEBAR ADMIN */}
        <DashboardSidebarAdmin />

        {/* AREA KANAN */}
        <div className="flex-1 flex flex-col min-w-0 h-full">
          {/* NAVBAR ADMIN */}
          <DashboardNavbarAdmin displayName={displayName} />

          {/* KONTEN: punya scroll sendiri */}
          <main className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
                    Revenue Ledger
                  </h1>
                  <p className="text-sm sm:text-base text-slate-600">
                    Riwayat alur keuangan dari setiap transaksi. Hanya tampilan
                    (read only).
                  </p>
                  {/* Total di UI (boleh tetap, beda dengan PDF) */}
                  <p className="mt-1 text-xs sm:text-sm text-slate-700">
                    Total saat ini:{" "}
                    <span className="font-semibold">
                      {formatRupiah(totalFilteredAmount)}
                    </span>
                  </p>
                </div>

                <div className="flex flex-col items-stretch sm:items-end gap-2">
                  {/* Filter tanggal */}
                  <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm">
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">Dari</span>
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                      />
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="text-slate-500">Sampai</span>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleResetFilter}
                      className="px-2.5 py-1 rounded-lg border border-slate-200 bg-white text-[11px] sm:text-xs font-medium text-slate-600 hover:bg-slate-50"
                    >
                      Reset
                    </button>
                  </div>

                  {/* Download PDF */}
                  <button
                    type="button"
                    onClick={handleDownloadPdf}
                    disabled={filteredEntries.length === 0}
                    className="inline-flex items-center justify-center px-3 py-1.5 rounded-xl bg-sky-600 text-white text-xs sm:text-sm font-semibold shadow-sm hover:bg-sky-700 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    📄 Download PDF
                  </button>
                </div>
              </div>

              {/* Error global */}
              {error && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs sm:text-sm text-rose-700">
                  {error}
                </div>
              )}

              {/* List revenue */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-3 sm:p-4 lg:p-6">
                {loadingData ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin h-8 w-8 border-4 border-sky-500 border-t-transparent rounded-full" />
                      <p className="text-xs sm:text-sm text-slate-500">
                        Memuat data revenue...
                      </p>
                    </div>
                  </div>
                ) : filteredEntries.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="text-sm text-slate-500 mb-1">
                      Tidak ada data yang cocok dengan filter.
                    </p>
                    <p className="text-xs text-slate-400">
                      Coba atur ulang filter tanggal atau lakukan transaksi
                      baru.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Page Size Selector */}
                    <div className="flex items-center gap-2 mb-4">
                      <span className="text-xs text-slate-500 font-medium">Baris per halaman:</span>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          const value = e.target.value === 'all' ? 'all' : Number(e.target.value);
                          setPageSize(value);
                          setCurrentPage(1);
                        }}
                        className="bg-white border border-slate-200 rounded-md px-3 py-1.5 text-xs font-medium text-slate-600 hover:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent cursor-pointer transition-all"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value="all">Semua ({filteredEntries.length})</option>
                      </select>
                    </div>

                    <div className="space-y-4">
                      {/* Desktop: tabel */}
                      <div className="hidden md:block overflow-x-auto -mx-3 sm:mx-0">
                        <table className="min-w-full text-sm">
                          <thead>
                            <tr className="text-xs text-slate-500 border-b border-slate-100">
                              <th className="text-left font-semibold py-2 px-3">
                                No
                              </th>
                              <th className="text-left font-semibold py-2 px-3">
                                Tanggal
                              </th>
                              <th className="text-left font-semibold py-2 px-3">
                                Transaction ID
                              </th>
                              <th className="text-left font-semibold py-2 px-3">
                                Wallet Owner
                              </th>
                              <th className="text-right font-semibold py-2 px-3">
                                Amount
                              </th>
                              <th className="text-left font-semibold py-2 px-3">
                                Deskripsi
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedEntries.map((e, idx) => {
                              const displayNo = pageSize === 'all'
                                ? idx + 1
                                : (currentPage - 1) * pageSize + idx + 1;
                              return (
                                <tr
                                  key={e.id}
                                  className="border-b border-slate-50 hover:bg-slate-50/60"
                                >
                                  <td className="py-3 px-3 align-top text-xs text-slate-500">
                                    {displayNo}
                                  </td>
                                  <td className="py-3 px-3 align-top text-xs text-slate-600 whitespace-nowrap">
                                    {formatDateTime(e.created_at)}
                                  </td>
                                  <td className="py-3 px-3 align-top text-xs text-slate-700">
                                    {e.transaction_id ?? "-"}
                                  </td>
                                  <td className="py-3 px-3 align-top text-xs text-slate-800">
                                    <div className="flex flex-col">
                                      <span className="truncate max-w-[200px]">
                                        {e.ownerName}
                                      </span>
                                      {e.ownerEmail && (
                                        <span className="text-[11px] text-slate-400 truncate max-w-[220px]">
                                          {e.ownerEmail}
                                        </span>
                                      )}
                                    </div>
                                  </td>
                                  <td className="py-3 px-3 align-top text-right text-sm font-semibold text-slate-900 whitespace-nowrap">
                                    {formatRupiah(e.amount)}
                                  </td>
                                  <td className="py-3 px-3 align-top text-xs text-slate-600">
                                    {e.description || "-"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile: card list */}
                      <div className="grid grid-cols-1 gap-3 md:hidden">
                        {paginatedEntries.map((e, idx) => {
                          const displayNo = pageSize === 'all'
                            ? idx + 1
                            : (currentPage - 1) * pageSize + idx + 1;
                          return (
                            <div
                              key={e.id}
                              className="rounded-2xl border border-slate-100 bg-slate-50/60 p-3 shadow-sm"
                            >
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <div>
                                  <p className="text-[11px] text-slate-500">
                                    {formatDateTime(e.created_at)}
                                  </p>
                                  <p className="text-xs font-semibold text-slate-800 truncate max-w-[200px]">
                                    No #{displayNo} • {e.ownerName}
                                  </p>
                                </div>
                                <p className="text-sm font-semibold text-slate-900">
                                  {formatRupiah(e.amount)}
                                </p>
                              </div>

                              <div className="mt-1 text-[11px] text-slate-600 space-y-0.5">
                                <p>
                                  <span className="font-semibold">
                                    Transaction:
                                  </span>{" "}
                                  {e.transaction_id ?? "-"}
                                </p>
                                {e.ownerEmail && (
                                  <p className="text-slate-400 truncate">
                                    {e.ownerEmail}
                                  </p>
                                )}
                              </div>

                              {e.description && (
                                <p className="mt-2 text-[11px] text-slate-600">
                                  {e.description}
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Pagination controls */}
                    {pageSize !== 'all' && (
                      <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs sm:text-sm text-slate-600">
                        <span>
                          Menampilkan{" "}
                          <span className="font-semibold">
                            {((currentPage - 1) * pageSize) + 1}
                          </span>
                          {" "}-{" "}
                          <span className="font-semibold">
                            {Math.min(currentPage * pageSize, filteredEntries.length)}
                          </span>
                          {" "}dari{" "}
                          <span className="font-semibold">
                            {filteredEntries.length}
                          </span>
                          {" "}entri
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={handlePrevPage}
                            disabled={currentPage === 1}
                            className="px-2.5 py-1 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                          >
                            ← Sebelumnya
                          </button>
                          <span>
                            Halaman{" "}
                            <span className="font-semibold">{currentPage}</span>{" "}
                            / {totalPages}
                          </span>
                          <button
                            type="button"
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages}
                            className="px-2.5 py-1 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-600 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                          >
                            Berikutnya →
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </main>
        </div>
      </div >
    </div >
  );
};

export default AdminRevenuePage;
